// El chat de un contratado como VENTANA sobre la oficina (S10): la oficina se ve atenuada detrás y la
// lista del equipo sigue a la izquierda para cambiar de agente. Cabecera como Telegram (avatar, nombre,
// línea de estado), la conversación, lo que te toca (FranjaAccion), el compositor y, a la derecha, la
// Información (se abre y se cierra). Enlazado a la URL (?agente=<id>); Esc cierra; Alt+↑/↓ cambia de agente.
import * as Menu from '@radix-ui/react-dropdown-menu';
import { ChevronLeft, DoorOpen, Info, MoreVertical, Pause, Pencil, Pin, PinOff, Search, X, type LucideIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { useAppStore } from '../../../stores/app.store';
import { stopAgentRequest } from '../../../lib/api';
import { useOficinaStore } from '../store';
import type { ContratadoDTO } from '../compartido/vocabulario';
import { mensajeError } from '../errores';
import { Boton, BotonIcono, IconoEncargo } from '../ui';
import { lineaDeEstado, quien } from '../marco/estado';
import { ahoraMismo, Informacion } from '../expediente/Informacion';
import { DialogoCerrar } from '../cierre/DialogoCerrar';
import { ConversacionOficina } from './ConversacionOficina';
import { Buscador } from './Buscador';
import { Compositor } from './Compositor';
import { useTeclasChat } from './useTeclasChat';
import { FranjaAccion } from './FranjaAccion';
import { useMarcarLeido } from '../leidos';
import { useDeslizarAtras } from './deslizarAtras';
import { TarjetasDelChat } from '../pelotas/TarjetaParaSanti';

// 13-10 — clave nueva: la ficha arranca abierta para todos (Pau: «me gusta la derecha: está siempre ahí»); lo que
// quedó guardado de la información vieja (que casi nadie abría) no cuenta.
const CLAVE_INFO = 'oficina:ficha-abierta';

/**
 * `movil` (la disposición de OficinaPage por debajo de 768 px): el chat ocupa la pantalla entera, «‹» a la
 * izquierda en vez de la ✕ y deslizar desde el borde izquierdo vuelve a la lista (09-10, maqueta aprobada).
 */
export function VentanaChat({ agentId, alCambiar, alCerrar, movil = false }: { agentId: string; alCambiar: (id: string) => void; alCerrar: () => void; movil?: boolean }) {
  const contratados = useOficinaStore((s) => s.contratados);
  const salas = useOficinaStore((s) => s.salas);
  const abrirChat = useOficinaStore((s) => s.abrirChat);
  const c = contratados[agentId];
  useMarcarLeido(c);
  // La ficha arranca abierta (13-10); después manda lo último que eligió Pau.
  // En el móvil (10-10, punto 11) la ficha tapa el chat entero: arranca siempre cerrada y no se recuerda; arriba
  // queda una línea con lo que necesita de ti que la abre (LineaFicha).
  const [info, setInfo] = useState(() => {
    if (movil) return false;
    return localStorage.getItem(CLAVE_INFO) !== '0';
  });
  // 13-10 — el buscador del chat (lupa de la cabecera); se cierra al cambiar de agente.
  const [buscando, setBuscando] = useState(false);
  useEffect(() => {
    setBuscando(false);
  }, [agentId]);
  // Esc y Alt+↑/↓ (en el orden que se ve en la lista, con «Con Santi»): useTeclasChat.
  useTeclasChat({ agentId, contratados, alCambiar, alCerrar });

  // El store solo recarga (y guarda) el expediente del chat en pantalla; al salir se olvida.
  useEffect(() => {
    abrirChat(agentId);
    return () => abrirChat(null);
  }, [abrirChat, agentId]);

  useEffect(() => {
    if (!movil) localStorage.setItem(CLAVE_INFO, info ? '1' : '0');
  }, [info, movil]);

  // Con la ficha abierta en el móvil, deslizar desde el borde vuelve a la conversación (como su «‹»), no a la lista.
  useDeslizarAtras(movil, info && movil ? () => setInfo(false) : alCerrar);

  const sala = salas.find((s) => s.projectId === c?.projectId)?.nombre ?? t('oficinaV2.desconocido');

  return (
    // El hueco y el fondo atenuado los pone el marco (Escenario): aquí solo la ventana, que lo llena.
    <section
        role="region"
        aria-label={c ? t('oficinaV2.chat.conversacionCon', { nombre: quien(c) }) : t('oficinaV2.chat.conversacion')}
        className={cn(
          'relative flex h-full w-full overflow-hidden bg-oficina-elevada animate-in fade-in-0 duration-300 ease-o-apple',
          movil ? '' : 'rounded-o-xl shadow-o-hoja zoom-in-[0.98]',
        )}
      >
        {c ? (
          <>
            <div className="flex min-w-0 flex-1 flex-col">
              <Cabecera
                contratado={c}
                info={info}
                alInfo={() => setInfo(!info)}
                alRenombrar={() => {
                  setInfo(true);
                  useOficinaStore.getState().pedirRenombrar(c.agentId);
                }}
                alCerrar={alCerrar}
                movil={movil}
                alBuscar={() => setBuscando(true)}
              />
              <Cuerpo
                contratado={c}
                alCambiar={alCambiar}
                movil={movil}
                alAbrirFicha={movil ? () => setInfo(true) : undefined}
                buscando={buscando}
                alCerrarBusqueda={() => setBuscando(false)}
              />
            </div>
            {info ? <PanelInformacion contratado={c} sala={sala} movil={movil} alVolver={() => setInfo(false)} /> : null}
          </>
        ) : (
          <div className="m-auto flex flex-col items-center gap-3 p-8 text-center">
            <p className="text-oficina-texto2">{t('oficinaV2.chat.sinAgente')}</p>
            <Boton onClick={alCerrar}>{t('oficinaV2.chat.volver')}</Boton>
          </div>
        )}
      </section>
  );
}

/**
 * Dónde va la Información. Escritorio: panel de 360 px a la derecha (encima del chat por debajo de 1024 px). Móvil
 * (10-10, punto 11 del informe de espacio): la pantalla entera con su «‹» — el panel de 360 px en 390 dejaba una
 * franja del chat a la izquierda que parecía un fallo.
 */
export function PanelInformacion({ contratado: c, sala, movil, alVolver }: { contratado: ContratadoDTO; sala: string; movil: boolean; alVolver: () => void }) {
  if (movil) {
    return (
      <div data-testid="panel-informacion" className="absolute inset-0 z-30 flex flex-col bg-oficina-elevada animate-in fade-in-0">
        <header className="flex h-16 shrink-0 items-center gap-1.5 border-b border-oficina-linea pl-1 pr-2">
          <BotonIcono icono={ChevronLeft} etiqueta={t('oficinaV2.info.volverAlChat')} onClick={alVolver} />
          <h2 className="text-o-titulo3 font-semibold text-oficina-texto">{t('oficinaV2.info.titulo')}</h2>
        </header>
        <div className="min-h-0 flex-1">
          <Informacion contratado={c} sala={sala} />
        </div>
      </div>
    );
  }
  return (
    <div data-testid="panel-informacion" className="absolute inset-y-0 right-0 z-20 w-[min(360px,100%)] border-l border-oficina-linea bg-oficina-panel shadow-o-hoja lg:static lg:shadow-none">
      <Informacion contratado={c} sala={sala} />
    </div>
  );
}

export function Cabecera({ contratado: c, info, alInfo, alRenombrar, alCerrar, movil, alBuscar }: {
  contratado: ContratadoDTO; info: boolean; alInfo: () => void; alRenombrar?: () => void; alCerrar: () => void; movil: boolean; alBuscar?: () => void;
}) {
  // Tras «Cambiar el nombre» el foco es del campo de la ficha: el menú no debe devolverlo a ⋮ al cerrarse.
  const renombrando = useRef(false);
  const [cerrando, setCerrando] = useState(false);
  const cierrePedido = useOficinaStore((s) => s.cierrePedido === c.agentId);
  useEffect(() => {
    if (!cierrePedido) return;
    useOficinaStore.getState().pedirCierre(null);
    setCerrando(true);
  }, [cierrePedido]);
  const [parando, setParando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const expediente = useOficinaStore((s) => s.expedientes[c.agentId]);
  const escribiendo = useAppStore((s) => s.agents[c.agentId]?.status === 'running');
  const estado = lineaDeEstado(c);
  return (
    <header className={cn('flex h-16 shrink-0 items-center gap-3 border-b border-oficina-linea bg-oficina-elevada pr-2', movil ? 'gap-1.5 pl-1' : 'pl-4')}>
      {/* 09-10 (maqueta aprobada) — en el móvil «‹» a la izquierda vuelve a la lista (como la ✕, que se esconde). */}
      {movil ? <BotonIcono icono={ChevronLeft} etiqueta={t('oficinaV2.chat.atras')} onClick={alCerrar} /> : null}
      <button type="button" onClick={alInfo} className="flex min-w-0 flex-1 items-center gap-3 rounded-o-s text-left outline-none focus-visible:ring-2 focus-visible:ring-oficina-enlace/70">
        <IconoEncargo icono={c.icono} semilla={c.semillaAvatar} tamano={42} />
        <span className="min-w-0">
          <span className="block truncate text-o-cuerpo font-semibold text-oficina-texto">
            {quien(c)}
          </span>
          <span
            className={cn(
              'block truncate text-o-sub',
              error ? 'text-oficina-error' : escribiendo && estado.tono === 'normal' ? 'text-oficina-enlace' : { acento: 'text-oficina-te-necesita', normal: 'text-oficina-texto2', aviso: 'text-oficina-bloqueado', error: 'text-oficina-error' }[estado.tono],
            )}
          >
            {error ?? (escribiendo && estado.tono === 'normal' ? t('oficinaV2.chat.escribiendo') : estado.texto)}
          </span>
        </span>
      </button>
      {alBuscar ? <BotonIcono icono={Search} etiqueta={t('oficinaV2.buscar.abrir')} onClick={alBuscar} /> : null}
      {escribiendo ? (
        <BotonIcono
          icono={Pause}
          etiqueta={parando ? t('oficinaV2.chat.interrumpiendo') : t('oficinaV2.chat.interrumpir')}
          disabled={parando}
          onClick={() => {
            setParando(true);
            setError(null);
            stopAgentRequest(c.agentId)
              .catch((e: unknown) => setError(mensajeError(e)))
              .finally(() => setParando(false));
          }}
        />
      ) : null}
      {/* 09-10 (maqueta aprobada «cabecera más limpia»): fijar e información van al menú ⋮, que está siempre.
          Crítica A9: «Terminar este trabajo» (irreversible) también ahí, lejos de la ✕ de cerrar la ventana. */}
      <Menu.Root>
        <Menu.Trigger asChild>
          <BotonIcono icono={MoreVertical} etiqueta={t('oficinaV2.chat.masAcciones')} />
        </Menu.Trigger>
        <Menu.Portal>
          <Menu.Content
            align="end"
            sideOffset={6}
            onCloseAutoFocus={(e) => {
              // En el móvil, devolver el foco al ⋮ enciende su pista «Más acciones» encima de lo que se acaba de abrir
              // (p. ej. la ficha a pantalla entera); con el dedo no hace falta devolverlo.
              if (renombrando.current || movil) e.preventDefault();
              renombrando.current = false;
            }}
            className="z-[70] min-w-[220px] rounded-o-m bg-oficina-elevada p-1 shadow-o-hoja ring-1 ring-white/10 animate-in fade-in-0 zoom-in-95">
            {/* 30-09 — fijar arriba (favoritos): el mismo «fijado» que la barra lateral del panel. */}
            <ItemMenu
              icono={c.fijado ? PinOff : Pin}
              onSelect={() => {
                void useAppStore.getState().pinAgent(c.agentId, !c.fijado).catch(() => setError(t('oficinaV2.fijar.error')));
              }}
            >
              {t(c.fijado ? 'oficinaV2.fijar.quitar' : 'oficinaV2.fijar.poner')}
            </ItemMenu>
            <ItemMenu icono={Info} onSelect={alInfo}>
              {t(info ? 'oficinaV2.info.ocultar' : 'oficinaV2.info.ver')}
            </ItemMenu>
            {/* 10-10 — el nombre lo pone la oficina y solo lo cambia una persona: aquí, donde Pau busca «qué hago con este». */}
            {alRenombrar ? (
              <ItemMenu
                icono={Pencil}
                onSelect={() => {
                  renombrando.current = true;
                  alRenombrar();
                }}
              >
                {t('oficinaV2.info.renombrar')}
              </ItemMenu>
            ) : null}
            {c.ciclo === 'activo' ? (
              <>
                <Menu.Separator className="mx-2 my-1 h-px bg-white/10" />
                <ItemMenu icono={DoorOpen} onSelect={() => setCerrando(true)}>
                  {t('oficinaV2.cierre.boton')}
                </ItemMenu>
              </>
            ) : null}
          </Menu.Content>
        </Menu.Portal>
      </Menu.Root>
      {movil ? null : (
        <>
          <span className="mx-1 h-6 w-px bg-white/10" aria-hidden />
          <BotonIcono icono={X} etiqueta={t('oficinaV2.chat.cerrar')} onClick={alCerrar} />
        </>
      )}
      <DialogoCerrar contratado={c} expediente={expediente} abierto={cerrando} alCerrar={() => setCerrando(false)} />
    </header>
  );
}

function ItemMenu({ icono: Icono, onSelect, children }: { icono: LucideIcon; onSelect: () => void; children: React.ReactNode }) {
  return (
    <Menu.Item
      onSelect={onSelect}
      className="flex min-h-11 cursor-pointer items-center gap-2.5 rounded-o-s px-3 py-2 text-o-cuerpo text-oficina-texto outline-none data-[highlighted]:bg-white/[0.06]"
    >
      <Icono className="h-4 w-4 text-oficina-texto2" aria-hidden />
      {children}
    </Menu.Item>
  );
}

/**
 * La línea fija de «qué pasa ahora» encima de lo que te toca y del compositor (13-10). En el móvil (Pau: «con el móvil,
 * si está arriba me molesta») es además el botón que abre la ficha: no hay nada encima del chat.
 */
export function LineaAhora({ contratado: c, alAbrirFicha }: { contratado: ContratadoDTO; alAbrirFicha?: () => void }) {
  const ficha = useOficinaStore((s) => s.expedientes[c.agentId]?.ficha ?? null);
  const a = ahoraMismo(c, ficha);
  const Contenedor = alAbrirFicha ? 'button' : 'div';
  return (
    <Contenedor
      {...(alAbrirFicha ? { type: 'button' as const, onClick: alAbrirFicha, 'aria-label': `${a.etiqueta}${a.texto ? ` · ${a.texto}` : ''} — ${t('oficinaV2.info.ver')}` } : { 'aria-live': 'polite' as const })}
      data-testid="linea-ahora"
      data-tono={a.tono}
      className={cn(
        // 03-11 (Apple): «ahora mismo» como una cápsula tranquila encima del compositor, no una franja de borde a borde.
        'mx-3 mt-1 flex min-h-10 shrink-0 items-center gap-2 rounded-o-m px-3.5 py-2 text-left text-o-texto sm:mx-5',
        a.tono === 'pide' ? 'bg-oficina-te-necesita/10 text-oficina-te-necesita-claro' : a.tono === 'problema' ? 'bg-oficina-situacion-problema-pau/10 text-oficina-error-claro' : 'bg-oficina-control/70 text-oficina-texto2',
      )}
    >
      <span
        aria-hidden
        className={cn(
          'h-2 w-2 shrink-0 rounded-full',
          a.tono === 'trabajando' ? 'animate-pulse bg-oficina-trabajando' : a.tono === 'pide' ? 'bg-oficina-te-necesita' : a.tono === 'problema' ? 'bg-oficina-error' : a.tono === 'oficina' ? 'bg-oficina-cerrando' : 'bg-oficina-texto3',
        )}
      />
      <span className="min-w-0 truncate">
        <span className={cn('font-semibold', a.tono === 'trabajando' && 'text-oficina-texto', a.tono === 'parado' && 'text-oficina-texto')}>{a.etiqueta}</span>
        {a.texto ? <span>{` · ${a.texto}`}</span> : null}
      </span>
      {alAbrirFicha ? <Info className="ml-auto h-4 w-4 shrink-0 opacity-70" aria-hidden /> : null}
    </Contenedor>
  );
}

function Cuerpo({ contratado: c, movil, alAbrirFicha, buscando = false, alCerrarBusqueda }: {
  contratado: ContratadoDTO; alCambiar: (id: string) => void; movil: boolean; alAbrirFicha?: () => void; buscando?: boolean; alCerrarBusqueda?: () => void;
}) {
  const agentId = c.agentId;
  const agent = useAppStore((s) => s.agents[agentId]);
  const expediente = useOficinaStore((s) => s.expedientes[agentId]);
  // Mientras se comprueba, revisa, publica o se recoge su mesa, un mensaje podría mover su trabajo después
  // de revisado (revisiones R1, V3): el compositor se sustituye por una franja. Mismo criterio que el servidor.
  const sinMensajes = expediente?.cierre.bloqueaMensajes ?? false;
  const messages = useAppStore((s) => s.messagesByAgent[agentId] ?? null);
  const loadMessages = useAppStore((s) => s.loadMessages);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    setError(null);
    loadMessages(agentId).catch((e: unknown) => vivo && setError(mensajeError(e)));
    return () => {
      vivo = false;
    };
  }, [agentId, loadMessages]);

  return (
    <>
      <div className="relative flex min-h-0 flex-1 flex-col">
        {error && messages === null ? (
          <p className="m-auto px-6 text-center text-o-texto text-oficina-error">{error}</p>
        ) : !agent || messages === null ? (
          <p className="m-auto text-o-texto text-oficina-texto3">{t('oficinaV2.chat.cargando')}</p>
        ) : (
          // 01-11 (pelotas v6): al final de la conversación, la tarjeta «Para Santi» que el agente deja lista y las ya enviadas.
          <ConversacionOficina contratado={c} mensajes={messages} traducciones={expediente?.ficha?.traducciones} resumenes={expediente?.ficha?.resumenes} pie={<TarjetasDelChat contratado={c} />} />
        )}
        {buscando && alCerrarBusqueda ? <Buscador contratado={c} alCerrar={alCerrarBusqueda} /> : null}
      </div>
      <LineaAhora contratado={c} alAbrirFicha={alAbrirFicha} />
      {/* 29-10 (Pau): fuera la barra «Siguiente: … Ir»; cada chat es solo la conversación. */}
      <FranjaAccion contratado={c} expediente={expediente} />
      {sinMensajes ? (
        <p
          className="shrink-0 border-t border-oficina-linea bg-oficina-elevada px-4 py-4 text-center text-o-texto text-oficina-texto2"
          style={movil ? { paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' } : undefined}
        >{t('oficinaV2.chat.cerrandoSinMensajes')}</p>
      ) : (
        <Compositor key={agentId} agentId={agentId} movil={movil} />
      )}
    </>
  );
}

